/** What the API of education answers (EDU-*), and the user's place in it. */
import { useApi } from './api'
import type { AnnotationData, Label } from './types'

export type EducationRole = 'educator' | 'learner'
export type TeachingPolicy = 'allowed' | 'approval' | 'forbidden'
export type ApprovalState = 'pending' | 'approved' | 'refused'
export type Outcome = 'found' | 'missed' | 'false' | 'disputed'
/** An assessment that measures a course's learning effect (EDU-08). */
export type Stage = 'entry' | 'final'

export interface EducationMe {
  roles: EducationRole[]
  /** The terms for learners to accept before the first case; null once accepted. */
  terms_version: string | null
  /** Agreed to the use of their pseudonymized results for educational research (EDU-13). */
  research_consent: boolean
  /** Where the learner trains, as they stated it, for the course statistics (EDU-11). */
  institution?: string | null
  country?: string | null
}

export interface Person {
  user: { id: string; username: string; display_name: string; email: string | null }
  role: EducationRole
  granted_at: string
  terms_accepted: boolean
}

export interface LearnerCaseSet {
  id: string
  name: string
  educator: string
  labels: string[]
  cases: number
  answered: number
  body_parts: { body_part: string | null; cases: number }[]
  course: { id: string; name: string } | null
  assessment: boolean
  stage: Stage | null
  /** Its course runs now, or it has none. */
  running: boolean
}

export interface CaseImage {
  id: string
  width: number
  height: number
  laterality: string | null
  view_position: string | null
}

export interface FeedbackMark {
  source: 'reference' | 'learner'
  /** A shape's ID, or a tag's label. */
  key: string
  outcome: Outcome
}

export interface CaseOutcome {
  found: number
  missed: number
  false: number
  positive: boolean
  marked: boolean
}

/** A numbered point of a teaching note on one of the case's images, in its pixels (EDU-05). */
export interface NoteMarker {
  image_id: string
  x: number
  y: number
}

export interface TeachingNote {
  text: string
  markers: NoteMarker[]
}

/** A released model's finding on an image, in its pixels (EDU-07). */
export interface Suggested {
  label: string
  confidence: number
  box: [number, number, number, number] | null
}

export interface CaseFeedback {
  images: Record<string, { reference: AnnotationData; marks: FeedbackMark[] }>
  outcome: CaseOutcome
  /** The educator's teaching note (EDU-07). */
  note?: TeachingNote | null
  /** The released model version whose suggestions follow, if the educator chose one. */
  model?: string | null
  suggestions?: Record<string, Suggested[]>
}

export interface TeachingCase {
  id: string
  age_years: number | null
  sex: string | null
  body_part: string | null
  images: CaseImage[]
  labels: Label[]
  answer: Record<string, AnnotationData> | null
  feedback: CaseFeedback | null
}

export interface Progress {
  cases: number
  answered: number
  /** Null in an assessment until every case is read (EDU-08). */
  found: number | null
  missed: number | null
  false: number | null
  sensitivity: number | null
  specificity: number | null
  /** The anonymous average of the course's learners (EDU-09). */
  course_average?: { learners: number; sensitivity: number | null; specificity: number | null } | null
}

export interface TeachingRelease {
  released_at: string | null
  cases: number
  sites: { site_id: string; site_name: string; policy: TeachingPolicy; state: ApprovalState | null }[]
}

export interface SiteTeaching {
  policy: TeachingPolicy
  releases: {
    frozen_collection_id: string
    frozen_collection_name: string
    cases: number
    released_at: string
    state: ApprovalState
  }[]
}

export interface CaseSet {
  collection_id: string
  labels: string[]
  cases: number
  course_id: string | null
  assessment: boolean
  stage: Stage | null
  model_id: string | null
}

/** A case of the educator's case set with its reference and teaching note (EDU-05). */
export interface EducatorCase {
  case: TeachingCase
  reference: Record<string, AnnotationData>
  note: TeachingNote | null
}

export interface CourseCaseSet {
  collection_id: string
  name: string
  assessment: boolean
  stage: Stage | null
}

/** A course over a period, with its educators, learners and case sets (EDU-01, EDU-02). */
export interface Course {
  id: string
  title: string
  description: string
  language: string
  starts_on: string
  ends_on: string
  /** The learners' readings are deleted after it; the statistics stay (EDU-14). */
  retention_until: string
  running: boolean
  /** The user is one of its educators. */
  teaching: boolean
  readings_deleted: boolean
  learners: number
  educators: Person['user'][]
  case_sets: CourseCaseSet[]
}

export interface CourseLearner {
  user: Person['user']
  /** The pseudonym of research exports (EDU-10). */
  code: string
  research_consent: boolean
  added_at: string
}

export interface CaseSetResult extends CourseCaseSet {
  answered: number
  found: number
  missed: number
  false: number
  sensitivity: number | null
  specificity: number | null
  seconds: number
}

export interface LearnerResult {
  user: Person['user']
  code: string
  research_consent: boolean
  case_sets: CaseSetResult[]
}

type Rates = { sensitivity: number | null; specificity: number | null }

/** A course's statistics for funding reports (EDU-11). */
export interface CourseReport {
  learners: number
  by_site: { site: string; country: string; learners: number }[]
  by_country: { country: string; learners: number }[]
  cases_read: number
  hours: number
  learning_effect: { learners: number; entry: Rates; final: Rates; change: Rates } | null
}

/** The user's roles in education; none while it loads. */
export function useEducation() {
  return useApi<EducationMe>('/education/me')
}

/** Where a case's images are opened: rendered views only (EDU-12, PRT-01). */
export const caseScope = (caseId: string) => `/education/cases/${caseId}/images`

/** Outcome colours on the feedback view: the theme's status colours (`--ok-text`, `--warn-text`,
 * `--danger`, `--dim`), as values, since SVG attributes take no custom properties. */
export const OUTCOME_COLORS: Record<Outcome, string> = {
  found: '#9ccb9f',
  missed: '#e3b873',
  false: '#e58b78',
  disputed: '#8a857c',
}

/** A released model's suggestions and the teaching note's markers on the feedback view. */
export const SUGGESTION_COLOR = '#8fb8ff'
export const MARKER_COLOR = '#ffd166'

/** A learner's sensitivity or specificity in whole percent; — before there is one. */
export const percent = (value: number | null) => (value === null ? '—' : `${Math.round(value * 100)} %`)
